import { DndContext, PointerSensor, TouchSensor, KeyboardSensor, closestCenter, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, arrayMove, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical, RotateCcw } from "lucide-react";

// Menu latéral réorganisable : applique un ordre enregistré aux rubriques AUTORISÉES uniquement (jamais d'ajout).
export function applyNavOrder(items, order) {
  if (!order?.length) return items;
  const rank = Object.fromEntries(order.map((k, i) => [k, i]));
  return [...items].sort((a, b) => (rank[a.key] ?? 999 + items.indexOf(a)) - (rank[b.key] ?? 999 + items.indexOf(b)));
}

function Row({ item, renderItem }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: item.key });
  const handle = (
    <span data-testid={`nav-handle-${item.key}`} {...attributes} {...listeners} onClick={(e) => e.stopPropagation()}
      className="ml-auto hidden md:grid place-items-center w-7 h-7 rounded-md text-stone-600 hover:text-[#C69C4A] cursor-grab active:cursor-grabbing touch-none" title="Glisser pour réorganiser">
      <GripVertical className="w-4 h-4" />
    </span>
  );
  return (
    <div ref={setNodeRef} style={{ transform: CSS.Transform.toString(transform), transition }} className={`${isDragging ? "opacity-70 z-10" : ""} md:w-full`}>
      {renderItem(item, handle)}
    </div>
  );
}

export function SortableNav({ items, order, onReorder, onReset, renderItem }) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 180, tolerance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const sorted = applyNavOrder(items, order);
  const onDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const keys = sorted.map((i) => i.key);
    onReorder(arrayMove(keys, keys.indexOf(active.id), keys.indexOf(over.id)));
  };
  return (
    <>
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
        <SortableContext items={sorted.map((i) => i.key)} strategy={verticalListSortingStrategy}>
          <div className="flex md:flex-col gap-1.5 md:w-full" data-testid="sortable-nav">
            {sorted.map((it) => <Row key={it.key} item={it} renderItem={renderItem} />)}
          </div>
        </SortableContext>
      </DndContext>
      {onReset && (
        <button data-testid="nav-reset-order" onClick={onReset} className="hidden md:flex items-center gap-1.5 text-[11px] text-stone-500 hover:text-[#C69C4A] px-3.5 py-1.5">
          <RotateCcw className="w-3 h-3" /> Réinitialiser l'ordre du menu
        </button>
      )}
    </>
  );
}
